import React, { useState, useEffect } from 'react';
import { teacherAPI } from '../services/api';
import { menuItems } from '../config/menuConfig';
import { toast } from 'react-toastify';
import { FiShield, FiSave, FiAlertCircle } from 'react-icons/fi';

const RolePermissions = () => {
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await teacherAPI.getFormData();
      if (response.data.success) {
        setRoles(response.data.data.roles);
      }
    } catch (error) {
      toast.error('Error loading roles');
    }
  };

  const handleRoleChange = async (e) => {
    const roleId = e.target.value;
    setSelectedRole(roleId);
    
    if (roleId) {
      setLoading(true);
      try {
        const response = await teacherAPI.getRolePermissions(roleId);
        if (response.data.success) {
          setPermissions(response.data.data || {});
        }
      } catch (error) {
        toast.error('Error loading permissions');
        setPermissions({});
      } finally {
        setLoading(false);
      }
    } else {
      setPermissions({});
    }
  };

  const handlePermissionChange = (menuId, action) => {
    setPermissions(prev => ({
      ...prev,
      [menuId]: {
        ...prev[menuId],
        menu_id: menuId,
        [action]: prev[menuId]?.[action] === 1 ? 0 : 1
      }
    }));
  };

  const handleSelectAll = (menuId, value) => {
    setPermissions(prev => ({
      ...prev,
      [menuId]: {
        menu_id: menuId,
        can_view: value,
        can_add: value,
        can_edit: value,
        can_delete: value
      }
    }));
  };

  const savePermissions = async () => {
    if (!selectedRole) {
      toast.error('Please select a role');
      return;
    }

    console.log('Saving permissions for role:', selectedRole);
    console.log('Permissions:', permissions);

    setSaving(true);
    try {
      const permissionsArray = Object.values(permissions);
      console.log('Permissions array:', permissionsArray);
      const response = await teacherAPI.updateRolePermissions(selectedRole, permissionsArray);
      
      if (response.data.success) {
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error saving permissions');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-full mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2">
          <FiShield className="text-primary-600" />
          Role Permissions
        </h1>
        <p className="text-sm md:text-base text-gray-600">
          Set default permissions for each role. These will apply to all staff members with the selected role.
        </p>
      </div>

      {/* Info Alert */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
        <FiAlertCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
        <div className="text-sm text-blue-800">
          <p className="font-medium mb-1">How it works:</p>
          <ul className="list-disc list-inside space-y-1 text-blue-700">
            <li>Select a role to configure permissions</li>
            <li>Check/uncheck permissions for each feature</li>
            <li>Click Save to apply permissions to ALL staff members with this role</li>
            <li>Each institute can set different permissions for the same role</li>
          </ul>
        </div>
      </div>

      {/* Role Selection */}
      <div className="bg-white rounded-lg shadow-md p-4 md:p-6 mb-6">
        <div className="max-w-md">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Role <span className="text-red-500">*</span>
          </label>
          <select
            value={selectedRole}
            onChange={handleRoleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
          >
            <option value="">-- Select a Role --</option>
            {roles.map((role) => (
              <option key={role.id} value={role.id}>{role.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Permissions Grid */}
      {selectedRole && (
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-4 md:p-6 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <h2 className="text-lg md:text-xl font-semibold">
                Permissions for {roles.find(r => r.id == selectedRole)?.name}
              </h2>
              <button
                onClick={savePermissions}
                disabled={saving}
                className="flex items-center justify-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                <FiSave />
                {saving ? 'Saving...' : 'Save Permissions'}
              </button>
            </div>
          </div>

          <div className="p-4 md:p-6">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
              </div>
            ) : (
              <div className="space-y-6">
                {menuItems.map(menu => {
                  // For single menu items with menuId
                  if (menu.path && menu.menuId) {
                    const perm = permissions[menu.menuId] || {};
                    const allChecked = perm.can_view === 1 && perm.can_add === 1 && 
                                      perm.can_edit === 1 && perm.can_delete === 1;
                    
                    return (
                      <div key={menu.id} className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-gray-50 px-4 py-3">
                          <h3 className="font-semibold text-gray-900">{menu.title}</h3>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full min-w-[600px]">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Feature</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">View</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Add</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Edit</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Delete</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">All</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="border-b border-gray-100 hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm text-gray-900">{menu.title}</td>
                                <td className="px-4 py-3 text-center">
                                  <input 
                                    type="checkbox" 
                                    checked={perm.can_view === 1} 
                                    onChange={() => handlePermissionChange(menu.menuId, 'can_view')}
                                    className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500 cursor-pointer" 
                                  />
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <input 
                                    type="checkbox" 
                                    checked={perm.can_add === 1}
                                    onChange={() => handlePermissionChange(menu.menuId, 'can_add')}
                                    className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500 cursor-pointer" 
                                  />
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <input 
                                    type="checkbox" 
                                    checked={perm.can_edit === 1}
                                    onChange={() => handlePermissionChange(menu.menuId, 'can_edit')}
                                    className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500 cursor-pointer" 
                                  />
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <input 
                                    type="checkbox" 
                                    checked={perm.can_delete === 1}
                                    onChange={() => handlePermissionChange(menu.menuId, 'can_delete')}
                                    className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500 cursor-pointer" 
                                  />
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <input 
                                    type="checkbox" 
                                    checked={allChecked}
                                    onChange={() => handleSelectAll(menu.menuId, allChecked ? 0 : 1)}
                                    className="w-4 h-4 text-green-600 rounded focus:ring-green-500 cursor-pointer" 
                                  />
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  }
                  
                  // For menu items with submenu
                  if (!menu.submenu) return null;
                  
                  return (
                    <div key={menu.id} className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                        <h3 className="font-semibold text-gray-900">{menu.title}</h3>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[600px]">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Feature</th>
                              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">View</th>
                              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Add</th>
                              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Edit</th>
                              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Delete</th>
                              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">All</th>
                            </tr>
                          </thead>
                          <tbody>
                            {menu.submenu.map(submenu => {
                              if (!submenu.menuId) return null;
                              
                              const perm = permissions[submenu.menuId] || {};
                              const allChecked = perm.can_view === 1 && perm.can_add === 1 && 
                                                perm.can_edit === 1 && perm.can_delete === 1;
                              
                              return (
                                <tr key={submenu.menuId} className="border-b border-gray-100 hover:bg-gray-50">
                                  <td className="px-4 py-3 text-sm text-gray-900">{submenu.title}</td>
                                  <td className="px-4 py-3 text-center">
                                    <input 
                                      type="checkbox" 
                                      checked={perm.can_view === 1}
                                      onChange={() => handlePermissionChange(submenu.menuId, 'can_view')}
                                      className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500 cursor-pointer" 
                                    />
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    <input 
                                      type="checkbox" 
                                      checked={perm.can_add === 1}
                                      onChange={() => handlePermissionChange(submenu.menuId, 'can_add')}
                                      className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500 cursor-pointer" 
                                    />
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    <input 
                                      type="checkbox" 
                                      checked={perm.can_edit === 1}
                                      onChange={() => handlePermissionChange(submenu.menuId, 'can_edit')}
                                      className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500 cursor-pointer" 
                                    />
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    <input 
                                      type="checkbox" 
                                      checked={perm.can_delete === 1}
                                      onChange={() => handlePermissionChange(submenu.menuId, 'can_delete')}
                                      className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500 cursor-pointer" 
                                    />
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    <input 
                                      type="checkbox" 
                                      checked={allChecked}
                                      onChange={() => handleSelectAll(submenu.menuId, allChecked ? 0 : 1)}
                                      className="w-4 h-4 text-green-600 rounded focus:ring-green-500 cursor-pointer" 
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RolePermissions;
